/**
 * Hero Slider
 *
 * Converts a .hero-slider or .is-slider gallery into an image slider.
 * Features are controlled by CSS classes:
 *   .has-autoplay    — auto-advance every 5 s
 *   .has-indicators  — progress bar indicators below the gallery
 *   .has-arrows      — prev/next navigation arrows
 *
 * If only one image exists, no slider UI is rendered.
 *
 * Expected markup: core/gallery with className "hero-slider" or "is-slider".
 * Each .wp-block-image inside is one slide.
 */

const INTERVAL = 5000;
const TRANSITION = 800; // ms, must match CSS transition duration

function initSlider( gallery ) {
	const slides = Array.from(
		gallery.querySelectorAll( ':scope > .wp-block-image' )
	);

	if ( slides.length <= 1 ) {
		return;
	}

	// Read feature flags from CSS classes
	const useAutoplay = gallery.classList.contains( 'has-autoplay' );
	const useIndicators = gallery.classList.contains( 'has-indicators' );
	const useArrows = gallery.classList.contains( 'has-arrows' );

	// Mark gallery as active slider
	gallery.classList.add( 'hero-slider--active' );

	let current = 0;
	let timer = null;

	function show( index ) {
		slides.forEach( ( slide, i ) => {
			slide.classList.toggle( 'is-active', i === index );
		} );
		if ( indicators.length ) {
			indicators.forEach( ( ind, i ) => {
				ind.classList.toggle( 'is-active', i === index );
			} );
		}
		current = index;
	}

	function next() {
		show( ( current + 1 ) % slides.length );
	}

	function prev() {
		show( ( current - 1 + slides.length ) % slides.length );
	}

	function goTo( index ) {
		show( index );
		resetTimer();
	}

	function resetTimer() {
		if ( timer ) {
			clearInterval( timer );
		}
		if ( useAutoplay ) {
			timer = setInterval( next, INTERVAL );
		}
	}

	// Build indicators
	let indicators = [];
	if ( useIndicators ) {
		const indicatorBar = document.createElement( 'div' );
		indicatorBar.className = 'hero-slider__indicators';
		slides.forEach( ( _, i ) => {
			const dot = document.createElement( 'div' );
			dot.className = 'hero-slider__indicator';
			dot.setAttribute( 'role', 'tab' );
			dot.setAttribute( 'aria-label', `Kuva ${ i + 1 }` );
			dot.addEventListener( 'click', () => goTo( i ) );
			indicatorBar.appendChild( dot );
		} );
		gallery.after( indicatorBar );
		indicators = Array.from(
			indicatorBar.querySelectorAll( '.hero-slider__indicator' )
		);
	}

	// Build arrows
	if ( useArrows ) {
		const prevBtn = document.createElement( 'button' );
		prevBtn.className = 'hero-slider__arrow hero-slider__arrow--prev';
		prevBtn.setAttribute( 'aria-label', 'Edellinen kuva' );
		prevBtn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
		prevBtn.addEventListener( 'click', () => {
			prev();
			resetTimer();
		} );

		const nextBtn = document.createElement( 'button' );
		nextBtn.className = 'hero-slider__arrow hero-slider__arrow--next';
		nextBtn.setAttribute( 'aria-label', 'Seuraava kuva' );
		nextBtn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
		nextBtn.addEventListener( 'click', () => {
			next();
			resetTimer();
		} );

		gallery.appendChild( prevBtn );
		gallery.appendChild( nextBtn );
	}

	// Init
	show( 0 );
	resetTimer();
}

function init() {
	document
		.querySelectorAll( '.hero-slider, .is-slider' )
		.forEach( initSlider );
}

// Run immediately if DOM already loaded (Cookiebot defers scripts),
// otherwise wait for DOMContentLoaded.
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
